// F13c-2 / FR-S7 / SD44: SCIM provisioning panel on the SSO admin page. scim.rs:
// GET /orgs/{org_id}/scim, POST /orgs/{org_id}/scim/tokens, DELETE /orgs/{org_id}/scim/tokens/{id}.
// Kept in its own file so it doesn't collide with #64's SsoSettingsPage changes.
import { useCallback, useEffect, useState } from "react";
import { Badge, Card } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import type { ScimStatusOut, ScimTokenCreated } from "../api/types";
import { Button, Notice } from "../components/ui";

function when(ts: string | null): string {
  return ts ? new Date(ts).toLocaleString() : "never";
}

export function ScimSection({ orgId }: { orgId: string }) {
  const [status, setStatus] = useState<ScimStatusOut | null>(null);
  const [created, setCreated] = useState<ScimTokenCreated | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api<ScimStatusOut>("GET", `/orgs/${orgId}/scim`)
      .then(setStatus)
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load SCIM status."));
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  async function onCreate() {
    setCreating(true);
    setError(null);
    try {
      setCreated(await api<ScimTokenCreated>("POST", `/orgs/${orgId}/scim/tokens`));
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't generate a SCIM token.");
    } finally {
      setCreating(false);
    }
  }

  async function onRevoke(id: string) {
    setError(null);
    try {
      await api("DELETE", `/orgs/${orgId}/scim/tokens/${id}`);
      if (created?.token.id === id) setCreated(null);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't revoke that token.");
    }
  }

  return (
    <Card size="sm" title="SCIM provisioning">
      <div className="admin-row-sub" style={{ marginBottom: 8 }}>
        Let your IdP create, update and deactivate members and sync groups to teams. Paste the base URL and a
        token into your IdP&apos;s SCIM settings. The token is shown once.
      </div>
      {status ? (
        <div className="list-row">
          <div className="list-row__body">
            <div className="list-row__title">SCIM base URL</div>
            <div className="list-row__sub mono" style={{ wordBreak: "break-all" }}>{status.base_url}</div>
          </div>
        </div>
      ) : null}
      <Button variant="secondary" onClick={onCreate} loading={creating} style={{ marginTop: 8 }}>
        Generate token
      </Button>
      {created ? (
        <Notice>
          <div>Copy this token now. It won&apos;t be shown again.</div>
          <div className="mono" style={{ wordBreak: "break-all" }} data-testid="scim-secret">{created.secret}</div>
        </Notice>
      ) : null}
      {error ? <Notice tone="error">{error}</Notice> : null}
      {status ? (
        <>
          <div className="list" style={{ marginTop: 10 }}>
            {status.tokens.map((t) => (
              <div className="list-row" key={t.id}>
                <div className="list-row__body">
                  <div className="list-row__title mono">{t.token_prefix}…</div>
                  <div className="list-row__sub">
                    Created {new Date(t.created_at).toLocaleDateString()} · last used {when(t.last_used_at)}
                  </div>
                </div>
                <Button variant="secondary" size="sm" onClick={() => onRevoke(t.id)}>
                  Revoke
                </Button>
              </div>
            ))}
            {status.tokens.length === 0 ? <div className="admin-row-sub">No active SCIM tokens.</div> : null}
          </div>
          <div className="admin-row-sub" style={{ marginTop: 10 }}>
            Last sync: {when(status.last_sync_at)}
          </div>
          {status.recent_activity.length > 0 ? (
            <div className="list" style={{ marginTop: 6 }}>
              {status.recent_activity.map((a, i) => (
                <div className="list-row" key={`${a.created_at}-${i}`}>
                  <div className="list-row__body">
                    <div className="list-row__title">
                      {a.event}
                      {a.target_type ? <Badge size="sm">{a.target_type}</Badge> : null}
                    </div>
                    <div className="list-row__sub">{new Date(a.created_at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}
